import {Component, ViewChild} from '@angular/core';
import {AlertController, IonDatetime, Platform} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import { zip } from 'rxjs';
import {finalize} from 'rxjs/operators';
import {LocalSettingsService} from 'shared/local-settings/local-settings.service';
import {Settings, UserProfile} from '../../model/model';
import {UserService} from '../../services/user.service';
import { LoginService } from 'app/login';
import { NavService } from 'shared/nav/nav.service';

import {Md5} from 'ts-md5';

import { Browser } from '@capacitor/browser';

import { LaunchReview } from '@awesome-cordova-plugins/launch-review/ngx';
import { DefaultMenuPage } from 'app/menu/components/default-menu-page/default-menu-page';

const NO_ALERT: string = '--:--';

@Component({
  selector: 'settings-page',
  templateUrl: 'settings-page.html',
  styleUrls: ['settings-page.scss'],
})
export class SettingsPage extends DefaultMenuPage {

  public isLoading: boolean = false;
  public dialogsTimesAutomatic: boolean = true;

  public theme: string;

  public allThemes: Array<string> = ['standard'];

  public mode: string;

  public lang: string;

  public displayShowLeaderOnly: boolean = false;

  public dialogsPickerOptionsFirst: any = this.createDialogsPickerOptions('timesDialogsFirst');

  public dialogsPickerOptionsSecond: any = this.createDialogsPickerOptions('timesDialogsSecond');

  public settings: Settings = {
    timesReflectionMorningAlert: '06:00',
    timesReflectionMorningDay: [true, true, true, true, true, false, false],
    timesReflectionEveningStart: '18:00',
    timesReflectionEveningAlert: '18:15',
    timesReflectionEveningDay: [true, true, true, true, true, false, false],
    timesReflectionResilienceStart: '18:00',
    timesReflectionResilienceAlert: '18:15',
    timesReflectionResilienceDay: [false, false, false, false, true, false, false],
    timesReflectionNightStart: '20:00',
    timesReflectionNightAlert: '20:15',
    timesReflectionNightDay: [true, true, true, true, true, false, false],
    timesDialogsFirst: undefined,
    timesDialogsSecond: undefined,
    showLeaderContent: false,
  };

  private _datePickerMorningAlert: IonDatetime;
  private _datePickerEveningAlert: IonDatetime;
  private _datePickerResilienceAlert: IonDatetime;
  private _datePickerNightAlert: IonDatetime;

  private _menuOn = {'corefacts': false, 'account': false};

  constructor(
    public navService: NavService,
    private translateService: TranslateService,
    private userService: UserService,
    private localSettingsService: LocalSettingsService,
    private loginService: LoginService,
    private alertCtrl: AlertController,
    private launchReview: LaunchReview,
    private platform: Platform,
  ) {
    super(navService, translateService);
    this.localSettingsService.get(LocalSettingsService.THEME)
      .then((result) => {if ( result ) { this.theme = result; }});
    this.localSettingsService.get(LocalSettingsService.LANGUAGE)
      .then((lang) => {if ( lang ) { this.lang = lang; }});
    this.localSettingsService.get(LocalSettingsService.MODE)
      .then((mode) => {if ( mode ) { this.mode = mode; }});
    this.translateService
      .get(['SETTINGS.TIMES.DEACTIVATED.BUTTON', 'SETTINGS.TIMES.AUTOMATIC.BUTTON', 'SETTINGS.TIMES.CANCEL', 'SETTINGS.TIMES.DONE'])
      .subscribe((text) => {
        this.setDialogTimesPickerTexts(this.dialogsPickerOptionsFirst, text);
        this.setDialogTimesPickerTexts(this.dialogsPickerOptionsSecond, text);
      });
  }

  private setDialogTimesPickerTexts(dialogsPickerOptions: any, text: Dictionary<string>): void {
    dialogsPickerOptions.buttons[0].text = text['SETTINGS.TIMES.AUTOMATIC.BUTTON'];
    dialogsPickerOptions.buttons[1].text = text['SETTINGS.TIMES.CANCEL'];
    dialogsPickerOptions.buttons[2].text = text['SETTINGS.TIMES.DONE'];
  }

  @ViewChild('morningAlert') public set datePickerMorningAlert(content: IonDatetime) {
    if (content) { // initially setter gets called with undefined
        this._datePickerMorningAlert = content;
    }
  }

  public get datePickerMorningAlert(): IonDatetime {
    return this._datePickerMorningAlert;
  }

  @ViewChild('eveningAlert') public set datePickerEveningAlert(content: IonDatetime) {
    if (content) { // initially setter gets called with undefined
        this._datePickerEveningAlert = content;
    }
  }

  public get datePickerEveningAlert(): IonDatetime {
    return this._datePickerEveningAlert;
  }

  @ViewChild('resilienceAlert') public set datePickerResilienceAlert(content: IonDatetime) {
    if (content) { // initially setter gets called with undefined
        this._datePickerResilienceAlert = content;
    }
  }

  public get datePickerResilienceAlert(): IonDatetime {
    return this._datePickerResilienceAlert;
  }

  @ViewChild('nightAlert') public set datePickerNightAlert(content: IonDatetime) {
    if (content) { // initially setter gets called with undefined
        this._datePickerNightAlert = content;
    }
  }

  public get datePickerNightAlert(): IonDatetime {
    return this._datePickerNightAlert;
  }

  private createDialogsPickerOptions(property: string): any {
    return {
      buttons: [
        {
          text: 'Autom.',
          handler: () => this.setDialogsTimesAutomatic(true),
        },
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Done',
          handler: (data) => {
            this.settings[property] = data.hour.text + ':' + data.minute.text;
          },
        },
      ],
    };
  }

  private ionViewWillEnter(): void {
    this.isLoading = true;
    this.userService.getUserProfile('full')
      .pipe(finalize(() => { this.isLoading = false; }))
      .subscribe((profile: UserProfile) => {
        if (profile.settings !== undefined) {
          this.settings = profile.settings;
          this.fixIncompleteSettings();
          if (this.settings.timesDialogsFirst) {
            this.dialogsTimesAutomatic = false;
          }
          /*if (this.settings.leading) {
            this.displayShowLeaderOnly = !profile.settings.leading.leading;
          }*/
        }
        if (!this.settings.timesReflectionMorningDay || this.settings.timesReflectionMorningDay.length === 0) {
          this.settings.timesReflectionMorningDay = [true, true, true, true, true, false, false];
        }
        if (!this.settings.timesReflectionEveningDay || this.settings.timesReflectionEveningDay.length === 0) {
          this.settings.timesReflectionEveningDay = [true, true, true, true, true, false, false];
        }
        if (!this.settings.timesReflectionResilienceDay || this.settings.timesReflectionResilienceDay.length === 0) {
          this.settings.timesReflectionResilienceDay = [false, false, false, false, true, false, false];
        }
        if (!this.settings.timesReflectionNightDay || this.settings.timesReflectionNightDay.length === 0) {
          this.settings.timesReflectionNightDay = [true, true, true, true, true, false, false];
        }
        const companyThemes: Array<string> = this.userService.getCompanyThemes(profile);
//        this.allThemes = companyThemes.concat(['standard', 'ocean', 'gold', 'dark']);
        this.allThemes = companyThemes.concat(['standard']);
      });
  }

  public themeChanged(): void {
    this.localSettingsService.set(LocalSettingsService.THEME, this.theme);
  }

  public langChanged(): void {
    this.localSettingsService.set(LocalSettingsService.LANGUAGE, this.lang);
  }

  public modeChanged(): void {
    this.localSettingsService.set(LocalSettingsService.MODE, this.mode);
    this.userService.setMode(this.mode);
  }

  private ionViewWillLeave() {
    // care for automatic
    if (this.dialogsTimesAutomatic) {
      this.settings.timesDialogsFirst = undefined;
      this.settings.timesDialogsSecond = undefined;
    }
    // save
    this.userService.setSettings(this.settings).subscribe();
  }

  public setDialogsTimesAutomatic(automatic: boolean): void {
    this.dialogsTimesAutomatic = automatic;
    if (!automatic) {
      if (!this.settings.timesDialogsFirst) {
        this.settings.timesDialogsFirst = '06:00';
      }
      if (!this.settings.timesDialogsSecond) {
        this.settings.timesDialogsSecond = '16:00';
      }
    }
  }

  private getTime(s: string): string {
    if (s.length <= 5) {
      return s;
    }
    const i: number = s.indexOf('T');
    if (i != -1) {
      return s.substr(i + 1, 5);
    }
    return s;
  }

  // care for anyhow affected settings
  private fixIncompleteSettings(): void {
    if (this.settings.timesReflectionMorningAlert === undefined) {
      this.settings.timesReflectionMorningAlert = '06:00';
    }
    if (this.settings.timesReflectionEveningStart === undefined) {
      this.settings.timesReflectionEveningStart = '18:00';
    }
    if (this.settings.timesReflectionEveningAlert === undefined) {
      this.settings.timesReflectionEveningAlert = '18:15';
    }
    if (this.settings.timesReflectionResilienceStart === undefined) {
      this.settings.timesReflectionResilienceStart = '18:00';
    }
    if (this.settings.timesReflectionResilienceAlert === undefined) {
      this.settings.timesReflectionResilienceAlert = '18:15';
    }
    if (this.settings.timesReflectionNightStart === undefined) {
      this.settings.timesReflectionNightStart = '20:00';
    }
    if (this.settings.timesReflectionNightAlert === undefined) {
      this.settings.timesReflectionNightAlert = '20:15';
    }
  }

  // Menu part

  public showPositionPage(): void {
    this.navService.push('onboarding/position', {isFromMenu: true});
  }

  public showPasswordSetPage(): void {
    this.navService.push('profile/passwordset');
  }

  public logout(): void {
    this.loginService.logout().subscribe((success) => {
      this.navService.root('login', {autoLogin: false});
    });
  }

  public unregister(): void {
    this.userService.getUserProfile('full')
    .subscribe((profile: UserProfile) => {
      Browser.open({url: 'https://register.leada.de/unregister/'+profile.email+'?code='+Md5.hashStr(profile._id)+'&lang='+this.translateService.currentLang});
    });
  }

  public toggleMenu(menu: string) {
    this._menuOn[menu] = !this._menuOn[menu];
  }

  public menuOn(menu: string) {
    return this._menuOn[menu] || this.mode == 'simple';
  }

  public openLink(link: string): void {
    this.translateService
      .get('HELP.LINKS.' + link)
      .subscribe((url: string) => {
        Browser.open({url: url.replace(/\u00AD/g, '')});
      });
  }

  public openGeneric(name: string): void {
    const text = 'HELP.TEXT.' + name;
    const title = 'MENU.COREFACTS.' + name;
    this.translateService
      .get([text, title])
      .subscribe((trans: Dictionary<string>) => {
        const s = trans[text];
        if (s.startsWith('https://') || s.startsWith('http://')) {
          Browser.open({url: s.replace(/\u00AD/g, '')});
        }
        else {
          this.navService.push('corefacts/generic', {type: trans[title], data: s});
        }
      });
  }
  
  // Help 
  public async showHelp(msgId: string): Promise<void> {
    zip(
      this.translateService.get('COMMON.HELP'),
      this.translateService.get('COMMON.OK'),
      this.translateService.get(msgId),
    ).subscribe(async(translations) => {
      const alert = await this.alertCtrl.create({
        cssClass: 'regular-alert',
        header: translations[0],
        buttons: [translations[1]],
        // tslint:disable-next-line:no-magic-numbers
        message: translations[2],
        backdropDismiss: true,
        animated: true,
      });
      alert.present();
    });
  }

  // Launch review

  public canLaunchReview(): boolean {
    return this.platform.is('capacitor');
  }

  public doLaunchReview(): void {
    console.log("** isSup: "+this.launchReview.isRatingSupported());
    this.markLaunchReviewDone();
    if(this.launchReview.isRatingSupported()){
      this.launchReview.rating().subscribe({
        next(x) {
          console.log('LaunchRating got value ' + x);
        },
        error(err) {
          console.error('LaunchRating something wrong occurred: ' + err);
        },
        complete() {
          console.log('LaunchRating done');
        },
      });
    }else{
      this.launchReview.launch().then(
          (x) => {
              console.log('LaunchReview got value ' + x);
            },
          (err) => {
            console.error('LaunchReview something wrong occurred: ' + err);
          }
      );
    }
  }

  private markLaunchReviewDone(): void {
    this.userService.markLaunchReviewDone().subscribe();
  }

}
